import { useState, useEffect } from 'react';
import { X, FileText, ShieldCheck, Warning, CheckCircle } from '@phosphor-icons/react';
import { Button } from '../components/ui/button';

// Policy content
const TERMS_CONTENT = `
## 1. Introduction

Welcome to BizCore™ and BizCore Pro™ ("the Platform"), a comprehensive business management and compliance solution developed and operated by SGK Apps ("Company", "we", "us", or "our").

By accessing or using our Platform, you agree to be bound by these Terms & Conditions. If you disagree with any part of these terms, you may not access the Platform.

## 2. Use of Service

### 2.1 Eligibility
You must be at least 18 years old and have the legal authority to enter into this agreement on behalf of yourself or your organization.

### 2.2 Account Registration
- You must provide accurate, complete, and current information during registration
- You are responsible for maintaining the confidentiality of your account credentials
- You must notify us immediately of any unauthorized use of your account

### 2.3 Acceptable Use
You agree not to:
- Use the Platform for any unlawful purpose
- Attempt to gain unauthorized access to any portion of the Platform
- Interfere with or disrupt the Platform's operations
- Upload malicious software or harmful content
- Share your account credentials with unauthorized parties

## 3. User Responsibilities

### 3.1 Data Accuracy
You are solely responsible for the accuracy, quality, and legality of data you input into the Platform, including but not limited to:
- Financial records and transactions
- Employee information
- Tax-related data
- Business documents

### 3.2 Compliance
You are responsible for ensuring your use of the Platform complies with all applicable laws and regulations in your jurisdiction, including:
- **India**: Goods and Services Tax (GST) laws, Income Tax Act, Companies Act 2013, and regulations issued by the Central Board of Indirect Taxes and Customs (CBIC)
- **UAE**: Federal Tax Authority (FTA) requirements for VAT and Corporate Tax
- Other applicable local, national, and international regulations

### 3.3 Tax Compliance Notice
**For Indian Users (GST):**
- You must ensure your GSTIN is valid and active
- HSN/SAC codes must be correctly applied to all goods and services
- GSTR-1, GSTR-3B, and other returns generated by the Platform must be verified before filing
- E-Invoice and E-Way Bill requirements must be independently verified

**For UAE Users (VAT):**
- Ensure TRN is correctly registered with FTA
- VAT returns must be verified before submission

### 3.4 Backup
While we maintain backups of Platform data, you are encouraged to maintain your own backups of critical business information.

## 4. Account Security

### 4.1 Password Protection
- Use strong, unique passwords for your account
- Enable multi-factor authentication when available
- Change passwords regularly

### 4.2 Access Control
- Assign appropriate roles and permissions to users within your organization
- Regularly review and update user access rights
- Immediately revoke access for terminated employees

## 5. Data Usage

### 5.1 Your Data
You retain all rights to the data you input into the Platform. We will not sell, rent, or share your data with third parties except as necessary to provide our services or as required by law.

### 5.2 Platform Improvements
We may use aggregated, anonymized data to improve our Platform and services. This data cannot be used to identify you or your business.

### 5.3 Analytics
We collect usage analytics to improve user experience and Platform performance.

## 6. Limitation of Liability

### 6.1 No Warranty
THE PLATFORM IS PROVIDED "AS IS" WITHOUT WARRANTIES OF ANY KIND, EXPRESS OR IMPLIED. WE DO NOT WARRANT THAT THE PLATFORM WILL BE UNINTERRUPTED, ERROR-FREE, OR COMPLETELY SECURE.

### 6.2 Limitation
TO THE MAXIMUM EXTENT PERMITTED BY LAW, SGK APPS SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, INCLUDING BUT NOT LIMITED TO:
- Loss of profits or revenue
- Loss of data
- Business interruption
- Tax penalties or fines resulting from errors in calculations

### 6.3 Maximum Liability
Our total liability shall not exceed the amount paid by you for the Platform services in the twelve (12) months preceding the claim.

## 7. Service Availability

### 7.1 Uptime
We strive to maintain 99.9% uptime but do not guarantee uninterrupted access to the Platform.

### 7.2 Maintenance
We may perform scheduled maintenance that temporarily affects Platform availability. We will provide advance notice when possible.

### 7.3 Force Majeure
We shall not be liable for any failure to perform due to circumstances beyond our reasonable control.

## 8. Modifications

### 8.1 Platform Changes
We reserve the right to modify, suspend, or discontinue any aspect of the Platform at any time.

### 8.2 Terms Updates
We may update these Terms & Conditions periodically. Continued use of the Platform after changes constitutes acceptance of the modified terms.

### 8.3 Notification
We will notify you of significant changes to these terms via email or Platform notification.

## 9. Governing Law

### 9.1 Jurisdiction
These Terms & Conditions shall be governed by and construed in accordance with:
- For Indian users: The laws of India, including the Information Technology Act, 2000 and applicable GST laws
- For UAE users: The laws of the United Arab Emirates
- For other users: The laws of the jurisdiction where the Company is incorporated

### 9.2 Dispute Resolution
**For Indian Users:**
Any disputes arising from these terms shall be subject to the exclusive jurisdiction of the courts in Mumbai, Maharashtra, India. Alternatively, disputes may be resolved through arbitration in accordance with the Arbitration and Conciliation Act, 1996.

**For UAE Users:**
Any disputes arising from these terms shall be resolved through arbitration in Dubai, UAE, in accordance with the rules of the Dubai International Arbitration Centre (DIAC).

## 10. Contact Information

For questions or concerns regarding these Terms & Conditions, please contact us at:

**SGK Apps**
Email: info@sgkapps.com
Website: bizcore.sgkapps.com

---

*These Terms & Conditions are effective as of the "Last Updated" date shown above.*
`;

const PRIVACY_CONTENT = `
## 1. Introduction

SGK Apps ("Company", "we", "us", or "our") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use BizCore™ and BizCore Pro™ ("the Platform").

## 2. Information We Collect

### 2.1 Personal Data
We collect information that identifies you personally, including:
- Full name
- Email address
- Phone number
- Job title and role
- Profile pictures (optional)

### 2.2 Business Data
We collect business-related information, including:
- Company name and registration details
- Tax Registration Number (TRN) for UAE / GSTIN for India
- PAN (Permanent Account Number) for Indian businesses
- CIN (Corporate Identity Number) for Indian companies
- Business addresses
- Industry classification
- Number of employees

### 2.3 Financial Data
We process financial information, including:
- Accounting records and transactions
- Invoice and payment data
- Bank account details (for payment processing)
- VAT records (UAE) / GST records (India)
- Income Tax and TDS details (India)
- Payroll information

### 2.4 Technical Data
We automatically collect:
- IP address
- Browser type and version
- Device information
- Access times and dates
- Pages viewed and features used

## 3. How We Use Your Data

### 3.1 Service Delivery
- Providing access to Platform features
- Processing transactions and payments
- Generating reports and documents
- Sending service-related notifications

### 3.2 Compliance Features
- VAT calculations and return generation (UAE)
- GST calculations, CGST/SGST/IGST computations, and GSTR generation (India)
- Corporate tax computations
- Compliance tracking and alerts
- Document management

### 3.3 Communications
- Account notifications
- Service updates
- Security alerts
- Marketing communications (with consent)

### 3.4 Platform Improvement
- Analyzing usage patterns
- Identifying and fixing bugs
- Developing new features
- Enhancing user experience

## 4. Data Protection

### 4.1 Encryption
- All data is encrypted in transit using TLS 1.3
- Sensitive data is encrypted at rest using AES-256
- Passwords are hashed using bcrypt

### 4.2 Access Control
- Role-based access control (RBAC)
- Multi-factor authentication options
- Session management and timeouts
- Audit logging of all access

### 4.3 Infrastructure Security
- Cloud hosting with enterprise-grade security
- Regular security assessments
- Intrusion detection systems
- DDoS protection

## 5. Data Sharing

### 5.1 No Selling of Data
We do not sell, rent, or trade your personal or business data to third parties.

### 5.2 Limited Third-Party Usage
We may share data with:
- Service providers who assist in Platform operations
- Payment processors for transactions
- Legal authorities when required by law
- Auditors for compliance verification

### 5.3 Business Transfers
In the event of a merger, acquisition, or sale, your data may be transferred to the new entity.

## 6. Cookies & Tracking

### 6.1 Essential Cookies
We use cookies necessary for Platform functionality:
- Authentication tokens
- Session management
- Security features

### 6.2 Analytics Cookies
With your consent, we use analytics to:
- Understand usage patterns
- Improve Platform performance
- Enhance user experience

### 6.3 Managing Cookies
You can control cookies through your browser settings. Disabling certain cookies may affect Platform functionality.

## 7. Your Rights

### 7.1 Access
You have the right to request a copy of your personal data held by us.

### 7.2 Correction
You can update or correct your personal information through your account settings or by contacting us.

### 7.3 Deletion
You may request deletion of your personal data, subject to legal retention requirements.

### 7.4 Data Portability
You can export your data in standard formats through the Platform.

### 7.5 Withdrawal of Consent
You may withdraw consent for data processing at any time, though this may affect your ability to use certain features.

## 8. Data Retention

### 8.1 Active Accounts
We retain your data for as long as your account is active and as needed to provide services.

### 8.2 Post-Termination
After account termination, we retain data for:
- **India**: 8 years for GST records (as per GST Act), 8 years for Income Tax records (as per Income Tax Act)
- **UAE**: 7 years for financial records (UAE legal requirement)
- 90 days for backup and recovery purposes
- As required by applicable law

### 8.3 Anonymization
After retention periods expire, data is securely deleted or anonymized.

## 9. International Transfers

Your data is stored in secure cloud servers. For Indian users, data is primarily stored in India-based servers to comply with data localization requirements. For UAE users, data is stored in UAE-based servers. If data transfer outside your country is necessary, we ensure appropriate safeguards are in place in compliance with applicable data protection laws.

## 10. Children's Privacy

The Platform is not intended for individuals under 18 years of age. We do not knowingly collect data from minors.

## 11. Changes to This Policy

We may update this Privacy Policy periodically. We will notify you of significant changes via email or Platform notification.

## 12. Contact Us

For privacy-related inquiries:

**SGK Apps - Data Protection**
Email: info@sgkapps.com
Website: bizcore.sgkapps.com

---

*This Privacy Policy is effective as of the "Last Updated" date shown above.*
`;

const DISCLAIMER_CONTENT = `
## Important Notice

Please read this disclaimer carefully before using BizCore™ and BizCore Pro™ ("the Platform"). By using the Platform, you acknowledge that you have read, understood, and agree to this disclaimer.

## 1. Software Assistance Disclaimer

### 1.1 Tool, Not Replacement
The Platform is a business management and compliance assistance tool. It is designed to help organize, calculate, and manage business operations, but it does not replace professional judgment or expertise.

### 1.2 Automation Limitations
While we strive for accuracy in all calculations and processes, automated systems may contain errors or may not account for all circumstances specific to your business.

## 2. No Guarantee of Tax Accuracy

### 2.1 Calculations
Tax calculations provided by the Platform, including but not limited to:
- **For India**: CGST, SGST, IGST calculations, GSTR-1, GSTR-3B generation, Input Tax Credit (ITC) computation, TDS calculations
- **For UAE**: VAT calculations and returns, Corporate tax computations
- Payroll tax deductions
- Withholding tax estimates

ARE PROVIDED AS GUIDANCE ONLY. We do not guarantee their accuracy, completeness, or compliance with current tax laws.

### 2.2 Tax Law Changes
Tax laws and regulations change frequently. While we strive to keep the Platform updated, there may be delays in reflecting recent changes. This is particularly important for:
- GST rate changes and notifications (India)
- E-Invoice and E-Way Bill threshold changes (India)
- VAT amendments (UAE)
- Corporate Tax updates

### 2.3 Interpretation
Tax regulations often require interpretation. The Platform's calculations are based on general interpretations and may not reflect the specific treatment applicable to your situation.

## 3. Not a Licensed Tax Advisor

### 3.1 No Professional Advice
SGK Apps and the Platform:
- Are NOT registered tax agents
- Are NOT licensed tax consultants
- Do NOT provide professional tax advice
- Are NOT authorized to represent you before tax authorities

### 3.2 No Client Relationship
Use of the Platform does not create a professional advisory relationship between you and SGK Apps.

## 4. User Responsibility for Filings

### 4.1 Ultimate Responsibility
YOU ARE SOLELY RESPONSIBLE FOR:
- The accuracy of data entered into the Platform
- Verifying all calculations before submission
- Ensuring compliance with all applicable laws
- Filing tax returns on time
- Paying taxes owed to authorities

### 4.2 Review Requirement
You must review all outputs, reports, and calculations generated by the Platform before relying on them or submitting them to any authority.

### 4.3 Record Keeping
You are responsible for maintaining proper records and documentation as required by law, regardless of Platform usage.

## 5. Recommendation to Consult Professionals

### 5.1 Professional Advice
We STRONGLY RECOMMEND that you:
- Consult with a licensed tax consultant or registered tax agent
- Seek legal advice for complex matters
- Engage qualified accountants for financial statements
- Verify compliance requirements with relevant authorities

### 5.2 Complex Situations
Professional consultation is especially important for:
- **India**: GST registration, Input Tax Credit claims, E-Invoice compliance, reverse charge mechanism, inter-state vs intra-state supply determination, SEZ transactions, export refunds
- **UAE**: First-time VAT registration, Corporate tax planning, Free zone tax treatment
- International transactions
- Group company arrangements
- Dispute resolution with tax authorities

## 6. Limitation of Liability

### 6.1 No Liability for Losses
SGK APPS SHALL NOT BE LIABLE FOR:
- Tax penalties, fines, or interest charges (including GST penalties under Section 122-138 of CGST Act or UAE VAT penalties)
- Incorrect tax filings or late submissions
- Business losses resulting from reliance on Platform outputs
- Decisions made based on Platform information
- Consequential or indirect damages of any kind
- E-Invoice or E-Way Bill generation errors (India)

### 6.2 Use at Your Own Risk
Your use of the Platform for tax and compliance purposes is entirely at your own risk.

### 6.3 Indemnification
You agree to indemnify and hold harmless SGK Apps from any claims, damages, or expenses arising from your use of the Platform for tax or compliance purposes.

## 7. Compliance with Local Laws

### 7.1 Regional Focus
The Platform is designed for businesses operating in India and UAE. While we strive for compliance:

**For Indian Users:**
- We do not guarantee compliance with all CBIC (Central Board of Indirect Taxes and Customs) requirements
- GST portal integration and return filing must be done independently on the official GST portal (gst.gov.in)
- E-Invoice and E-Way Bill generation through government portals is the user's responsibility
- Income Tax compliance (Form 16, TDS returns) must be verified independently

**For UAE Users:**
- We do not guarantee compliance with all UAE Federal Tax Authority (FTA) requirements
- VAT returns must be filed through the official FTA portal

### 7.2 Other Jurisdictions
The Platform may not be suitable for use in jurisdictions outside the UAE. Users in other regions must verify compliance with local requirements.

## 8. Updates and Changes

### 8.1 Platform Updates
We may update Platform features and calculations without notice. Such updates do not constitute confirmation that previous calculations were incorrect.

### 8.2 Disclaimer Updates
We may update this disclaimer at any time. Continued use constitutes acceptance of changes.

## 9. Acknowledgment

BY USING THE PLATFORM, YOU ACKNOWLEDGE THAT:

1. You have read and understood this disclaimer
2. The Platform is a tool and not a replacement for professional advice
3. You are responsible for verifying all outputs
4. SGK Apps is not liable for tax-related issues
5. Professional consultation is recommended

## 10. Contact

For questions about this disclaimer:

**SGK Apps**
Email: info@sgkapps.com
Website: bizcore.sgkapps.com

---

*This Disclaimer is effective as of the "Last Updated" date shown above.*
`;

// Legal Modal Component
export function LegalModal({ isOpen, onClose, type, onAccept }) {
  const [scrolledToBottom, setScrolledToBottom] = useState(false);

  const titles = {
    terms: 'Terms & Conditions',
    privacy: 'Privacy Policy',
    disclaimer: 'Disclaimer'
  };

  const icons = {
    terms: FileText,
    privacy: ShieldCheck,
    disclaimer: Warning
  };

  const content = {
    terms: TERMS_CONTENT,
    privacy: PRIVACY_CONTENT,
    disclaimer: DISCLAIMER_CONTENT
  };

  const Icon = icons[type] || FileText;

  const handleScroll = (e) => {
    const { scrollTop, scrollHeight, clientHeight } = e.target;
    if (scrollTop + clientHeight >= scrollHeight - 50) {
      setScrolledToBottom(true);
    }
  };

  useEffect(() => {
    setScrolledToBottom(false);
  }, [type, isOpen]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[90vh] flex flex-col overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200 bg-slate-50">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-[#1e3a5f] flex items-center justify-center">
              <Icon size={22} className="text-[#d4a84b]" weight="duotone" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-[#1e3a5f]">{titles[type]}</h2>
              <p className="text-xs text-slate-500">Version 1.0 | Last Updated: March 27, 2025</p>
            </div>
          </div>
          <button 
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-slate-200 transition-colors"
          >
            <X size={20} className="text-slate-500" />
          </button>
        </div>

        {/* Content */}
        <div 
          className="flex-1 overflow-y-auto px-6 py-4 prose prose-slate prose-sm max-w-none"
          onScroll={handleScroll}
          style={{ maxHeight: 'calc(90vh - 180px)' }}
        >
          <div className="whitespace-pre-wrap text-slate-700 text-sm leading-relaxed">
            {content[type]?.split('\n').map((line, i) => {
              if (line.startsWith('## ')) {
                return <h2 key={i} className="text-lg font-bold text-[#1e3a5f] mt-6 mb-3">{line.replace('## ', '')}</h2>;
              } else if (line.startsWith('### ')) {
                return <h3 key={i} className="text-base font-semibold text-slate-800 mt-4 mb-2">{line.replace('### ', '')}</h3>;
              } else if (line.startsWith('- ')) {
                return <li key={i} className="ml-4 text-slate-600">{line.replace('- ', '')}</li>;
              } else if (line.startsWith('**') && line.endsWith('**')) {
                return <p key={i} className="font-semibold text-slate-800">{line.replace(/\*\*/g, '')}</p>;
              } else if (line.trim() === '') {
                return <br key={i} />;
              } else {
                return <p key={i} className="text-slate-600 my-1">{line}</p>;
              }
            })}
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-6 py-4 border-t border-slate-200 bg-slate-50">
          <p className="text-xs text-slate-500">
            {scrolledToBottom ? (
              <span className="flex items-center gap-1 text-green-600">
                <CheckCircle size={14} weight="fill" />
                You've read the entire document
              </span>
            ) : (
              'Scroll to read the entire document'
            )}
          </p>
          <div className="flex gap-3">
            <Button 
              variant="outline" 
              onClick={onClose}
              className="border-slate-300"
            >
              Close
            </Button>
            {onAccept && (
              <Button 
                onClick={onAccept}
                className="bg-[#1e3a5f] hover:bg-[#15293f] text-white"
              >
                Accept & Continue
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// Legal Page Component for standalone pages
export default function LegalPage({ type = 'terms' }) {
  const titles = {
    terms: 'Terms & Conditions',
    privacy: 'Privacy Policy',
    disclaimer: 'Disclaimer'
  };

  const content = {
    terms: TERMS_CONTENT,
    privacy: PRIVACY_CONTENT,
    disclaimer: DISCLAIMER_CONTENT
  };

  return (
    <div className="min-h-screen bg-slate-50 py-12 px-4">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2 mb-4">
            <span className="text-2xl font-black text-[#1e3a5f]">BIZ</span>
            <span className="text-2xl italic font-medium text-[#1e3a5f]">Core</span>
            <span className="text-sm text-[#d4a84b]">™</span>
          </div>
          <h1 className="text-3xl font-bold text-[#1e3a5f] mb-2">{titles[type]}</h1>
          <p className="text-slate-500">Version 1.0 | Last Updated: March 27, 2025</p>
        </div>

        {/* Content Card */}
        <div className="bg-white rounded-2xl shadow-lg p-8 prose prose-slate max-w-none">
          {content[type]?.split('\n').map((line, i) => {
            if (line.startsWith('## ')) {
              return <h2 key={i} className="text-xl font-bold text-[#1e3a5f] mt-8 mb-4">{line.replace('## ', '')}</h2>;
            } else if (line.startsWith('### ')) {
              return <h3 key={i} className="text-lg font-semibold text-slate-800 mt-6 mb-3">{line.replace('### ', '')}</h3>;
            } else if (line.startsWith('- ')) {
              return <li key={i} className="ml-4 text-slate-600">{line.replace('- ', '')}</li>;
            } else if (line.startsWith('**') && line.endsWith('**')) {
              return <p key={i} className="font-semibold text-slate-800">{line.replace(/\*\*/g, '')}</p>;
            } else if (line.trim() === '') {
              return <br key={i} />;
            } else {
              return <p key={i} className="text-slate-600 my-1">{line}</p>;
            }
          })}
        </div>

        {/* Footer */}
        <div className="text-center mt-8 text-sm text-slate-500">
          <p>© 2025 SGK Apps. All rights reserved.</p>
          <a href="https://bizcore.sgkapps.com" className="text-[#d4a84b] hover:underline">bizcore.sgkapps.com</a>
        </div>
      </div>
    </div>
  );
}

// Export individual page components
export function TermsPage() {
  return <LegalPage type="terms" />;
}

export function PrivacyPage() {
  return <LegalPage type="privacy" />;
}

export function DisclaimerPage() {
  return <LegalPage type="disclaimer" />;
}
